import { Link } from 'react-router-dom';
import { BrandLogo, GitHubMark } from '@shared/brand';
import { cn } from '@shared/lib/utils';

export interface MarketingFooterColumn {
  ariaLabel: string;
  heading: string;
  links: { to: string; label: string; external?: boolean }[];
}

interface MarketingFooterProps {
  tagline: string;
  copyright: string;
  columns: MarketingFooterColumn[];
  localeSwitcher: React.ReactNode;
  /** Optional GitHub repository link rendered under the brand tagline. */
  repoLink?: { href: string; label: string };
}

const footerHeadingClass =
  'text-xs font-semibold uppercase tracking-wide text-foreground';
const footerLinkClass =
  'text-sm text-muted-foreground transition-colors hover:text-foreground';

const containerClass = 'mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8';

/**
 * Shared marketing footer (G-09). Renders a `n+1`-column grid:
 * brand + tagline + one column per `columns` entry.
 */
export const MarketingFooter = ({
  tagline,
  copyright,
  columns,
  localeSwitcher,
  repoLink,
}: MarketingFooterProps) => (
  <footer className="border-t py-12 text-muted-foreground">
    <div
      className={cn(containerClass, 'grid gap-8 sm:grid-cols-2')}
      style={{
        gridTemplateColumns: `repeat(${Math.max(columns.length + 1, 1)}, minmax(0, 1fr))`,
      }}
    >
      <div>
        <BrandLogo className="inline-flex items-center gap-2 font-bold text-foreground" />
        <p className="mt-3 max-w-prose text-sm text-muted-foreground">{tagline}</p>
        {repoLink ? (
          <a
            href={repoLink.href}
            target="_blank"
            rel="noreferrer"
            className={cn(footerLinkClass, 'mt-3 inline-flex items-center gap-2')}
          >
            <span className="size-4 shrink-0" aria-hidden="true">
              <GitHubMark />
            </span>
            {repoLink.label}
          </a>
        ) : null}
      </div>
      {columns.map((column) => (
        <nav
          key={column.heading}
          className="flex flex-col gap-2"
          aria-label={column.ariaLabel}
        >
          <h3 className={footerHeadingClass}>{column.heading}</h3>
          {column.links.map((link) =>
            link.external ? (
              <a
                key={link.to}
                href={link.to}
                target="_blank"
                rel="noreferrer"
                className={footerLinkClass}
              >
                {link.label}
              </a>
            ) : (
              <Link key={link.to} to={link.to} className={footerLinkClass}>
                {link.label}
              </Link>
            ),
          )}
        </nav>
      ))}
    </div>
    <div
      className={cn(
        containerClass,
        'mt-10 flex flex-col gap-4 border-t pt-6 sm:flex-row sm:items-center sm:justify-between',
      )}
    >
      <p className="text-sm text-muted-foreground">{copyright}</p>
      {localeSwitcher}
    </div>
  </footer>
);
